import { useAppStore } from '@core/store'
import { setupDoEstado } from './store/clashSlice'
import { temDecisao } from './clashTriage'
import { useClashPartilha } from './clashPartilhaEstado'
import {
  construirSeccaoConflitos,
  triagensDiferentes,
  type VnSessionConflitos,
} from './clashSessao'
import {
  CLASH_TRIAGE_KEY,
  leituraBloqueada,
  loadClassificacao,
  loadCorridas,
  loadSetup,
  loadTriage,
  type ClashSetupFile,
} from './clashTriageStorage'

// ---------------------------------------------------------------------------
// A ponte entre o ficheiro de sessao e o estado dos conflitos (store e
// localStorage). A parte pura (formato, leitura, comparacao) esta em
// clashSessao.ts.
// ---------------------------------------------------------------------------

/** A seccao dos conflitos a gravar na sessao, a partir do que esta em
 *  memoria (ou, se o painel nunca abriu, do que esta em localStorage). */
export function seccaoConflitosActual(): VnSessionConflitos | null {
  const s = useAppStore.getState()
  // Com a recuperacao por decidir, a memoria e o trabalho desta sessao
  // (o anterior nao se le): grava-se o que esta em memoria.
  const daMemoria = s.clashTriageHydrated || leituraBloqueada()
  const corridas = daMemoria
    ? {
        historico: s.clashHistorico,
        projectoBcf: s.clashProjectoBcf,
        chavesUltimaCorrida: s.clashChavesUltima,
        modulo: s.clashModulo,
        ultimaExportacao: s.clashUltimaExportacao,
      }
    : loadCorridas()
  const setup: ClashSetupFile | null = s.clashSetupHydrated ? setupDoEstado(s) : loadSetup()
  const activa = useClashPartilha.getState().activa
  return construirSeccaoConflitos({
    setup,
    triagem: daMemoria ? s.clashTriage : loadTriage(),
    ...corridas,
    classificacao: daMemoria ? s.clashClassificacao : loadClassificacao(),
    partilha: activa ? { projetoId: activa.projetoId, codigo: activa.codigo } : null,
  })
}

export type ResultadoDaReposicao = 'reposta' | 'mantida'

/**
 * Repoe a seccao dos conflitos de uma sessao. A triagem que ja esta no
 * browser so e substituida se o utilizador o confirmar, e so se pergunta
 * quando as duas sao diferentes e a do browser nao esta vazia.
 */
export function reporSeccaoConflitos(
  sec: VnSessionConflitos,
  confirmar: (mensagem: string) => boolean,
): ResultadoDaReposicao {
  const s = useAppStore.getState()
  const { partilha, ...resto } = sec
  // Uma seccao so com a indicacao da triagem partilhada nao traz
  // trabalho para repor: so a oferta de entrar.
  const soPartilha = !construirSeccaoConflitos({ ...resto, partilha: null })
  if (soPartilha) {
    oferecerPartilha(partilha ?? null, 'mantida')
    return 'mantida'
  }
  const actual = s.clashTriageHydrated ? s.clashTriage : loadTriage()
  const nActual = Object.keys(actual).length
  if (nActual > 0 && triagensDiferentes(actual, sec.triagem)) {
    const nSessao = Object.keys(sec.triagem).length
    const ok = confirmar(
      `A sessão traz a triagem dos conflitos (${nSessao} parecer${nSessao === 1 ? '' : 'es'}) `
        + `e este browser tem outra (${nActual} parecer${nActual === 1 ? '' : 'es'}). `
        + 'Substituir a triagem do browser pela da sessão? '
        + 'Cancelar mantém a do browser e ignora a da sessão.',
    )
    if (!ok) {
      oferecerPartilha(partilha ?? null, 'mantida')
      return 'mantida'
    }
  }
  s.restaurarConflitos(sec)
  oferecerPartilha(partilha ?? null, 'reposta')
  return 'reposta'
}

/**
 * Depois de abrir uma sessao: se ela pertence a uma triagem partilhada
 * em que este browser nao esta, oferece entrar; se trouxe uma triagem e
 * nao ha partilha activa, oferece partilha-la no workspace. A barra da
 * partilha so mostra a oferta com sessao num workspace com o modulo.
 */
function oferecerPartilha(
  partilha: { projetoId: string; codigo: string } | null,
  resultado: ResultadoDaReposicao,
): void {
  const p = useClashPartilha.getState()
  if (partilha) {
    if (p.activa?.projetoId !== partilha.projetoId) {
      p.definir({ oferta: { tipo: 'entrar', projetoId: partilha.projetoId, codigo: partilha.codigo } })
    }
    return
  }
  if (resultado !== 'reposta' || p.activa) return
  const n = Object.values(useAppStore.getState().clashTriage).filter(temDecisao).length
  if (n > 0) p.definir({ oferta: { tipo: 'partilhar', pareceres: n } })
}

/** A chave da triagem nao vai no `local_work` de uma sessao nova: tem a
 *  sua seccao (e a sua confirmacao). */
export function semTriagemDosConflitos<T extends { key: string }>(itens: readonly T[]): T[] {
  return itens.filter((w) => w.key !== CLASH_TRIAGE_KEY)
}
